/**
 * Notification Models
 *
 * This file contains notification-related models and interfaces
 * for the real-time notification system using SignalR.
 */

import { AlertDto, AlertCategory, AlertSeverity, AlertType, Role } from '../dto/api-dtos';

/**
 * Notification interface extending AlertDto with additional app-level properties
 */
export interface Notification extends AlertDto {
  /** Whether the notification is currently being processed */
  isProcessing?: boolean;
  /** Parsed payload object if payload is valid JSON */
  parsedPayload?: Record<string, unknown>;
}

/**
 * Notification state for the store
 */
export interface NotificationState {
  /** List of all notifications */
  notifications: Notification[];
  /** List of unread notifications */
  unreadNotifications: Notification[];
  /** Total count of unread notifications */
  unreadCount: number;
  /** Whether SignalR connection is established */
  isConnected: boolean;
  /** Whether connection is in progress */
  isConnecting: boolean;
  /** Connection error message if any */
  connectionError: string | null;
  /** Last notification received timestamp */
  lastNotificationAt: Date | null;
}

/**
 * SignalR connection status
 */
export enum ConnectionStatus {
  Disconnected = 'disconnected',
  Connecting = 'connecting',
  Connected = 'connected',
  Reconnecting = 'reconnecting',
  Disconnecting = 'disconnecting',
}

/**
 * Notification action types for handling notifications
 */
export interface NotificationAction {
  /** Action type identifier */
  type: string;
  /** Action label */
  label: string;
  /** Action handler function */
  handler?: (notification: Notification) => void;
}

/**
 * Notification filter options
 */
export interface NotificationFilter {
  /** Filter by category */
  category?: AlertCategory;
  /** Filter by severity */
  severity?: AlertSeverity;
  /** Filter by type */
  type?: AlertType;
  /** Filter by succession file ID */
  successionFileId?: number;
  /** Show only unread notifications */
  unreadOnly?: boolean;
  /** Search term for title/message */
  searchTerm?: string;
}

/**
 * Notification preferences
 */
export interface NotificationPreferences {
  /** Enable desktop notifications */
  enableDesktopNotifications: boolean;
  /** Enable sound notifications */
  enableSound: boolean;
  /** Categories to show notifications for */
  enabledCategories: AlertCategory[];
  /** Minimum severity level to show */
  minSeverity: AlertSeverity;
}

/**
 * Create Alert Request DTO
 * Used when creating a new alert via the API
 */
export interface CreateAlertRequest {
  /** Identifiant du dossier de succession (optionnel) */
  successionFileId: string | null;
  /** Alert category */
  category: AlertCategory;
  /** Alert type */
  type: AlertType;
  /** Alert severity */
  severity: AlertSeverity;
  /** Titre de l'alerte */
  title: string | null;
  /** Message détaillé */
  message: string | null;
  /** Données JSON additionnelles (optionnel) */
  payload: string | null;
  /** Role for which the alert is intended */
  role: Role;
  /** Liste des IDs utilisateurs à notifier */
  userIds: string[] | null;
  /** ID tenant pour multi-tenant (optionnel) */
  tenantId: string | null; // uuid
  /** Clé de déduplication (optionnel) */
  dedupeKey: string | null;
  /** Date d'expiration de l'alerte (optionnel) */
  expiresAt: string | null; // date-time
  /** Identifiant de l'événement source (optionnel) */
  sourceEventId: string | null; // uuid
}

/**
 * Pagination information structure
 * Matches the pagination object in API responses
 */
export interface PaginationInfo {
  pageNumber: number;
  pageSize: number;
  count: number;
  countPages: number;
  hasPreviousPage: boolean;
  hasNextPage: boolean;
}

/**
 * Paginated Alert DTO Response
 * Used for paginated alert search results
 * Wrapper for paginated collections result using skip/take approach
 */
export interface AlertDtoApiPaginated {
  /** The request status */
  status: string | null;
  /** Associated user friendly message */
  message: string | null;
  /** Pagination information */
  pagination: PaginationInfo | null;
  /** Array of alert items */
  data: AlertDto[] | null;
  /** Total count */
  count: number;
}

